import { useState, useEffect, useMemo } from 'react'
import type { TunerExport, TunerNote } from '../store/tuningStore'
import { computeCentsScale } from '../utils/noteUtils'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Check } from 'lucide-react'

interface TunerExportModalProps {
  tunerData: TunerExport
  onExport: (selectedNotes: TunerNote[]) => void
  onClose: () => void
}

function centsColorClass(cents: number): string {
  if (cents > 0) return 'text-blue-600'
  if (cents < 0) return 'text-rose-600'
  return 'text-muted-foreground'
}

function centsDotClass(cents: number): string {
  if (cents > 0) return 'bg-blue-600'
  if (cents < 0) return 'bg-rose-600'
  return 'bg-muted-foreground'
}

function formatCentsText(cents: number): string {
  if (cents === 0) return '0'
  return cents > 0 ? `+${cents.toFixed(1)}` : cents.toFixed(1)
}

export const TunerExportModal = ({ tunerData, onExport, onClose }: TunerExportModalProps) => {
  const [selectedNotes, setSelectedNotes] = useState<Set<number>>(new Set())

  // Select all notes by default
  useEffect(() => {
    setSelectedNotes(new Set(tunerData.notes.map((_, index) => index)))
  }, [tunerData])

  const scale = useMemo(() => computeCentsScale(tunerData.notes.map((n) => n.cents)), [tunerData])
  const zeroPercent = ((0 - scale.min) / (scale.max - scale.min)) * 100

  const toggleNote = (index: number) => {
    const newSelected = new Set(selectedNotes)
    if (newSelected.has(index)) {
      newSelected.delete(index)
    } else {
      newSelected.add(index)
    }
    setSelectedNotes(newSelected)
  }

  const selectAll = () => {
    setSelectedNotes(new Set(tunerData.notes.map((_, index) => index)))
  }

  const deselectAll = () => {
    setSelectedNotes(new Set())
  }

  const handleExport = () => {
    const notesToExport = tunerData.notes.filter((_, index) => selectedNotes.has(index))
    onExport(notesToExport)
    onClose()
  }

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [onClose])

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Exporter au format Tuner</DialogTitle>
          <DialogDescription>
            Sélectionnez les notes à afficher sur l'image · triées du grave à l'aigu
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2 mb-2">
          <Button onClick={selectAll} variant="outline" size="sm">
            Tout sélectionner
          </Button>
          <Button onClick={deselectAll} variant="outline" size="sm">
            Tout désélectionner
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto -mx-1 px-1">
          <div className="flex flex-col gap-1">
            {tunerData.notes.map((note, index) => {
              const isSelected = selectedNotes.has(index)
              const pct = ((note.cents - scale.min) / (scale.max - scale.min)) * 100

              return (
                <button
                  key={index}
                  onClick={() => toggleNote(index)}
                  className={`w-full flex items-center gap-3 px-2.5 py-2 rounded-lg text-left transition-colors ${
                    isSelected ? 'bg-primary/5' : 'hover:bg-muted'
                  }`}
                >
                  <div
                    className={`w-4 h-4 rounded border shrink-0 flex items-center justify-center ${
                      isSelected ? 'bg-primary border-primary' : 'border-border'
                    }`}
                  >
                    {isSelected && <Check className="w-3 h-3 text-primary-foreground" />}
                  </div>

                  <div className="w-16 shrink-0">
                    <div className="text-sm font-semibold leading-tight">{note.name}</div>
                    <div className="text-[10px] text-muted-foreground leading-tight">{note.nameEn}</div>
                  </div>

                  <div className="w-14 shrink-0">
                    {note.isTonic && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary whitespace-nowrap">
                        tonique
                      </span>
                    )}
                    {note.isExtra && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground whitespace-nowrap">
                        ajoutée
                      </span>
                    )}
                  </div>

                  <div className="flex-1 relative h-1.5 bg-muted rounded-full min-w-[80px]">
                    <div
                      className="absolute -top-1 h-3.5 w-px bg-border"
                      style={{ left: `${zeroPercent}%` }}
                    />
                    <div
                      className={`absolute top-1/2 w-2.5 h-2.5 rounded-full -translate-x-1/2 -translate-y-1/2 ${centsDotClass(note.cents)}`}
                      style={{ left: `${pct}%` }}
                    />
                  </div>

                  <div className={`w-16 shrink-0 text-right text-xs font-semibold ${centsColorClass(note.cents)}`}>
                    {formatCentsText(note.cents)}¢
                  </div>
                  <div className="w-20 shrink-0 text-right text-[10px] text-muted-foreground">
                    {note.frequency.toFixed(1)} Hz
                  </div>
                </button>
              )
            })}
          </div>
        </div>

        <div className="flex items-center justify-between pt-4 border-t">
          <div className="text-sm font-semibold text-muted-foreground">
            {selectedNotes.size} note{selectedNotes.size > 1 ? 's' : ''} sélectionnée{selectedNotes.size > 1 ? 's' : ''}
          </div>
          <div className="flex gap-2">
            <Button
              onClick={onClose}
              variant="outline"
            >
              Annuler
            </Button>
            <Button
              onClick={handleExport}
              disabled={selectedNotes.size === 0}
            >
              Exporter ({selectedNotes.size})
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
